import React from "react";
import {
  Users, ShieldCheck, AlertTriangle, ShieldX, HelpCircle, Info,
  History,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * CeberCard — Cluster Early-Buyer Entity Recurrence (F2).
 *
 * Surfaces the CEBER modifier from /api/scan:
 *   report.risk_modifiers.ceber = {
 *     enabled, ok, band: "PASS"|"WARN"|"FAIL"|null,
 *     penalty, skipped_reason,
 *     details: {
 *       overlap_pct, overlap_count, buyers_sampled,
 *       rugged_cohorts_compared, matched_rugs: [..],
 *     } | {} when skipped
 *   }
 *
 * Cold-start state `skipped_reason === "insufficient_history"` is shown
 * EXPLICITLY (per PR #10) so the user understands the modifier is alive
 * but waiting for the rug-cohort DB to fill.
 */

function bandMeta(band) {
  switch (band) {
    case "PASS":
      return { label: "Clean cohort", variant: "success", icon: ShieldCheck };
    case "WARN":
      return { label: "Repeat early buyers", variant: "warning", icon: AlertTriangle };
    case "FAIL":
      return { label: "Serial rug cohort", variant: "danger", icon: ShieldX };
    default:
      return { label: "Not scored", variant: "muted", icon: HelpCircle };
  }
}

function humanizeSkippedReason(reason) {
  if (!reason) return "";
  if (reason === "disabled") return "CEBER is disabled on the backend.";
  if (reason === "insufficient_history") {
    return (
      "Cold-start: the rug-cohort database is still filling from the digest service. " +
      "CEBER will activate once ≥25 confirmed-rugged Solana tokens have been collected (~25 alerts that drop ≥80% within 24h)."
    );
  }
  if (reason === "no_buyers") return "Helius returned no early buyers for this mint.";
  if (reason === "no_helius_key") return "HELIUS_API_KEY missing on the backend.";
  if (reason.startsWith("rpc_error:")) return `RPC error: ${reason.slice("rpc_error:".length)}`;
  return reason;
}

export function CeberCard({ ceber }) {
  if (!ceber) return null;
  const meta = bandMeta(ceber.band);
  const Icon = meta.icon;
  const d = ceber.details || {};
  const reasonText = humanizeSkippedReason(ceber.skipped_reason);
  const isColdStart = ceber.skipped_reason === "insufficient_history";
  const isScored = ceber.band === "PASS" || ceber.band === "WARN" || ceber.band === "FAIL";
  const matched = Array.isArray(d.matched_rugs) ? d.matched_rugs : [];

  // For cold-start, lean on the History icon — it's not a failure, it's
  // a "we're learning" state and should look distinct from generic skipped.
  const HeaderIcon = isColdStart ? History : Users;

  return (
    <Card className="p-0" data-testid="ceber-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <HeaderIcon size={16} className="mt-0.5 text-[hsl(var(--muted))]" />
            <div>
              <CardTitle>Creator eligibility · Early-buyer cohort</CardTitle>
              <CardDescription>
                Cross-checks the first wallets to buy this mint against wallets
                that bought confirmed-rugged tokens.
              </CardDescription>
            </div>
          </div>
          <Badge
            variant={isColdStart ? "primary" : meta.variant}
            className="gap-1"
          >
            {isColdStart ? (
              <>
                <History size={12} /> Learning
              </>
            ) : (
              <>
                <Icon size={12} /> {meta.label}
              </>
            )}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isColdStart ? (
          <div
            className="rounded-md border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.06)] px-3 py-3"
            data-testid="ceber-coldstart"
          >
            <div className="flex items-start gap-2 text-xs text-[hsl(var(--fg))]">
              <Info size={13} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
              <span>{reasonText}</span>
            </div>
          </div>
        ) : !isScored ? (
          <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
            <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted))]">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{reasonText || "CEBER could not score this token."}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Metric
                label="Overlap"
                value={`${(d.overlap_pct ?? 0).toFixed(1)}%`}
                testId="ceber-overlap-pct"
              />
              <Metric
                label="Repeat wallets"
                value={`${d.overlap_count ?? 0}`}
                testId="ceber-overlap-count"
              />
              <Metric
                label="Cohorts compared"
                value={`${d.rugged_cohorts_compared ?? 0}`}
                testId="ceber-cohorts"
              />
            </div>

            <div className="text-[11px] text-[hsl(var(--muted))]">
              Sampled the first{" "}
              <span className="font-mono text-[hsl(var(--fg))] tabular-nums">
                {d.buyers_sampled ?? 0}
              </span>{" "}
              buyer wallets for this mint.
            </div>

            {matched.length > 0 ? (
              <>
                <Separator />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))] mb-1.5">
                    Matched rugged tokens (sample)
                  </div>
                  <ul className="space-y-1 text-[11px] font-mono">
                    {matched.map((m) => (
                      <li
                        key={m}
                        className="truncate text-[hsl(var(--muted))]"
                        title={m}
                      >
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : null}

            {ceber.penalty ? (
              <>
                <Separator />
                <div className="text-[11px] text-[hsl(var(--muted))]">
                  Score penalty applied:{" "}
                  <span className="font-mono text-[hsl(var(--warning))]">
                    −{ceber.penalty}
                  </span>
                </div>
              </>
            ) : null}
          </>
        )}

        <p className="text-[10px] text-[hsl(var(--muted))]">
          Bands key on overlap %: PASS if very low, WARN at moderate overlap, FAIL when
          a significant share of the cohort previously bought rugged tokens.
        </p>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div
      className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {label}
      </div>
      <div className="mt-1 font-mono text-base tabular-nums">{value}</div>
    </div>
  );
}

export default CeberCard;
